import { siteConfig } from "@/config/site.config";

/** Build a full absolute URL for canonical / og:url */
export function pageUrl(path = "/") {
    const base = siteConfig.siteUrl.replace(/\/$/, "");
    return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

/** Merge default SEO with per-page overrides */
export function resolveSeo(seo = {}, path = "/") {
    const merged = {
        ...siteConfig.defaultSeo,
        ...seo,
    };
    if (!merged.canonicalUrl) merged.canonicalUrl = pageUrl(path);
    if (!merged.ogTitle) merged.ogTitle = merged.title;
    if (!merged.ogDescription) merged.ogDescription = merged.description;
    if (!merged.ogImage) merged.ogImage = siteConfig.defaultSeo.ogImage;
    return merged;
}

/** Build organisation / local business JSON-LD */
export function organisationSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": pageUrl("/#organization"),
        name: siteConfig.siteName,
        legalName: siteConfig.registeredCompanyName,
        url: siteConfig.siteUrl,
        email: siteConfig.email,
        telephone: siteConfig.mobile,
        foundingDate: String(siteConfig.yearEstablished),
        image: siteConfig.defaultSeo.ogImage,
        identifier: siteConfig.uen,
        address: {
            "@type": "PostalAddress",
            streetAddress: `${siteConfig.address.line1}, ${siteConfig.address.line2}, ${siteConfig.address.unit}`,
            addressLocality: "Singapore",
            postalCode: "608586",
            addressCountry: "SG",
        },
        areaServed: "Singapore",
    };
}

/** Breadcrumb JSON-LD */
export function breadcrumbSchema(items = []) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: it.label,
            item: pageUrl(it.href || "/"),
        })),
    };
}

/** FAQ JSON-LD */
export function faqSchema(faqs = []) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
    };
}

/** Product JSON-LD (no price/review since we don't invent pricing) */
export function productSchema(product) {
    return {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        sku: product.referenceNumber,
        description: product.shortDescription,
        image: product.images?.map((i) => i.url) || [product.featuredImage],
        category: product.categoryName,
        brand: { "@type": "Brand", name: siteConfig.siteName },
    };
}

/** Blog article JSON-LD */
export function blogPostingSchema(post) {
    return {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: post.title,
        description: post.excerpt,
        image: post.featuredImage,
        datePublished: post.publishedAt,
        dateModified: post.updatedAt || post.publishedAt,
        author: { "@type": "Organization", name: post.author || siteConfig.siteName },
        publisher: { "@type": "Organization", name: siteConfig.siteName },
        keywords: post.tags?.join(", "),
    };
}
